import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useMemo, useState, type ReactElement } from 'react';
import { Columns3, List } from 'lucide-react';
import { Alert, Button, Select, Tag, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import type { ListTasksFilters, TaskItem, TaskStatus } from '../api/tasks.client.js';
import { TaskFormModal } from '../components/TaskFormModal.js';
import { TaskKanbanBoard } from '../components/TaskKanbanBoard.js';
import { TasksGrid } from '../components/TasksGrid.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useTaskGridProfile } from '../hooks/useTaskGridProfile.js';
import { useMembers } from '../hooks/useTaskLookups.js';
import { useTasks } from '../hooks/useTasks.js';
import { useDeleteTaskMutation, useUpdateTaskMutation } from '../hooks/useTaskMutations.js';
import { useTaskStatusMutation } from '../hooks/useTaskStatusMutation.js';
import { buildTaskKanbanColumns } from '../lib/taskKanban.js';
import { getTaskStatusLabel, getTaskStatusOptions } from '../lib/taskMeta.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch } from '../../../shared/ui/FilterBar.js';
import '../components/task-detail.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import {
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  SEG_TRACK_STYLE,
  segOptionStyle,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';

type GroupBy = 'none' | 'status' | 'deal' | 'company' | 'contact';
/** Серверный дедлайн-фильтр: границы дат считает страница, отбор — сервер. */
type DeadlineFilter = 'all' | 'overdue' | 'today' | 'week';
type TaskViewMode = 'list' | 'kanban';

const DAY_MS = 86_400_000;

function contactLabel(task: TaskItem): string {
  if (!task.contact) return i18n.t('crm.features.features.tasks.pages.taskspage.без_контакта');
  return [task.contact.firstName, task.contact.lastName].filter(Boolean).join(' ');
}

function groupLabel(groupBy: GroupBy, task: TaskItem): string {
  switch (groupBy) {
    case 'status':
      return getTaskStatusLabel(task.status);
    case 'deal':
      return task.deal?.title ?? i18n.t('crm.features.features.tasks.pages.taskspage.без_сделки');
    case 'company':
      return (
        task.company?.name ?? i18n.t('crm.features.features.tasks.pages.taskspage.без_компании')
      );
    case 'contact':
      return contactLabel(task);
    default:
      return '';
  }
}

export default function TasksPage(): ReactElement {
  useI18nSubscription();
  const locale = i18n.locale;
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | undefined>(undefined);
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [deadlineFilter, setDeadlineFilter] = useState<DeadlineFilter>('all');
  const [viewMode, setViewMode] = useState<TaskViewMode>('list');

  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);
  const [initialStatus, setInitialStatus] = useState<TaskStatus | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const grid = useTaskGridProfile();

  // Все фильтры — серверные: клиентский фильтр применялся бы к усечённой выборке.
  // Дедлайн-фильтр считает только границы дат; сам отбор по срокам выполняет сервер.
  const listFilters = useMemo((): ListTasksFilters => {
    if (viewMode !== 'list') return {};
    const filters: ListTasksFilters = {
      ...(q.trim() ? { q } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(assigneeFilter ? { assigneeMemberId: assigneeFilter } : {}),
    };
    if (deadlineFilter === 'overdue') {
      filters.overdue = true;
    } else if (deadlineFilter === 'today') {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      filters.dueFrom = start.toISOString();
      filters.dueTo = end.toISOString();
    } else if (deadlineFilter === 'week') {
      // Start at the beginning of today: tasks due earlier today still belong
      // to "this week" and must not silently drop out of the list.
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      filters.dueFrom = start.toISOString();
      filters.dueTo = new Date(start.getTime() + 7 * DAY_MS).toISOString();
    }
    return filters;
  }, [assigneeFilter, deadlineFilter, q, statusFilter, viewMode]);
  const { data, isLoading, isError, error, refetch } = useTasks(listFilters);
  const membersQuery = useMembers();

  const updateMut = useUpdateTaskMutation();
  const statusMutation = useTaskStatusMutation();
  const deleteMut = useDeleteTaskMutation();

  const rows = useMemo(() => data ?? [], [data]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map<string, TaskItem[]>();
    for (const task of rows) {
      const label = groupLabel(groupBy, task);
      const bucket = map.get(label);
      if (bucket) bucket.push(task);
      else map.set(label, [task]);
    }
    return Array.from(map.entries());
  }, [rows, groupBy, locale]);

  const kanbanColumns = useMemo(() => buildTaskKanbanColumns(rows), [rows, locale]);

  const openCreate = (status: TaskStatus = 'todo'): void => {
    setEditingTask(null);
    setInitialStatus(status);
    setFormOpen(true);
  };
  const openEdit = (task: TaskItem): void => {
    setInitialStatus(undefined);
    setEditingTask(task);
    setFormOpen(true);
  };
  const closeForm = (): void => {
    setFormOpen(false);
    setEditingTask(null);
    setInitialStatus(undefined);
  };

  // Публикуем выделение — быстрая заметка цепляется к отмеченным задачам.
  usePublishSelection(
    'task',
    rows
      .filter((task) => selectedIds.includes(task.id))
      .map((task) => ({ id: task.id, label: task.title })),
  );

  const gridProps = {
    profile: grid,
    selectedIds,
    onSelectionChange: setSelectedIds,
    onEdit: openEdit,
    onDelete: (task: TaskItem) => deleteMut.mutate(task.id),
    onQuickStatus: (task: TaskItem, status: TaskStatus) =>
      updateMut.mutate({ id: task.id, input: { status } }),
    quickStatusPendingId: updateMut.isPending ? (updateMut.variables?.id ?? null) : null,
    deletePendingId: deleteMut.isPending ? (deleteMut.variables ?? null) : null,
  };

  const mutationError = (updateMut.error ??
    statusMutation.error ??
    deleteMut.error) as Error | null;

  return (
    <div className="animate-fade-in flex flex-col">
      {/* Header Area */}
      <PageHeader
        title={i18n.t('crm.features.features.tasks.pages.taskspage.задачи')}
        subtitle={
          <>
            <span data-testid="tasks-visible-count">{rows.length}</span>{' '}
            {i18n.tc('crm.features.task_detail.count', rows.length)} ·{' '}
            {i18n.t(
              'crm.features.features.tasks.pages.taskspage.создавайте_и_ведите_задачи_все_в_пределах_',
            )}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <div
              className="flex items-center overflow-hidden p-0.5"
              role="group"
              style={SEG_TRACK_STYLE}
            >
              <Tooltip title={i18n.t('crm.features.features.tasks.pages.taskspage.канбан')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.tasks.pages.taskspage.канбан_2')}
                  aria-pressed={viewMode === 'kanban'}
                  onClick={() => setViewMode('kanban')}
                  style={segOptionStyle(viewMode === 'kanban', true)}
                >
                  <Columns3 size={14} />
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t('crm.features.features.tasks.pages.taskspage.список')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.tasks.pages.taskspage.список_2')}
                  aria-pressed={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
                  style={segOptionStyle(viewMode === 'list', false)}
                >
                  <List size={14} />
                </Button>
              </Tooltip>
            </div>
            {viewMode === 'list' ? <GridColumnsButton profile={grid} /> : null}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCreate()}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {i18n.t('crm.features.features.tasks.pages.taskspage.новая_задача')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void refetch()}
              style={LIST_ICON_BUTTON_STYLE}
              aria-label={i18n.t('crm.common.reload')}
            />
          </div>
        }
        testId="tasks-page-header"
      >
        {/* Панель фильтров/группировки — entity-detail standard (#865):
            FilterBar + FilterSearch; визуальный язык D — hairline-бордюр и
            surface-заливка внутри самого FilterBar. */}
        {viewMode === 'list' ? (
          <div className="tasks-list-filters">
            <FilterBar
              search={
                <FilterSearch
                  aria-label={i18n.t(
                    'crm.features.features.tasks.pages.taskspage.поиск_по_названию',
                  )}
                  placeholder={i18n.t(
                    'crm.features.features.tasks.pages.taskspage.поиск_по_названию',
                  )}
                  value={q}
                  onChange={setQ}
                />
              }
            >
              <Select<TaskStatus | 'all'>
                aria-label={i18n.t('crm.features.features.tasks.pages.taskspage.статус')}
                value={statusFilter ?? 'all'}
                style={{ width: 170, fontSize: 12 }}
                onChange={(value) => setStatusFilter(value === 'all' ? undefined : value)}
                options={[
                  {
                    value: 'all',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.все_статусы'),
                  },
                  ...getTaskStatusOptions(),
                ]}
              />
              <Select<string | null>
                aria-label={i18n.t('crm.features.features.tasks.pages.taskspage.любой_исполнитель')}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={i18n.t(
                  'crm.features.features.tasks.pages.taskspage.любой_исполнитель',
                )}
                style={{ width: 190, fontSize: 12 }}
                value={assigneeFilter}
                loading={membersQuery.isLoading}
                onChange={(value) => setAssigneeFilter(value ?? null)}
                options={(membersQuery.data ?? []).map((m) => ({
                  value: m.id,
                  label: m.name || m.email,
                }))}
              />
              <Select<GroupBy>
                aria-label={i18n.t('crm.features.task_detail.group_by')}
                value={groupBy}
                style={{ width: 170, fontSize: 12 }}
                onChange={setGroupBy}
                options={[
                  {
                    value: 'none',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.без_группировки'),
                  },
                  {
                    value: 'status',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.по_статусу'),
                  },
                  {
                    value: 'deal',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.по_сделке'),
                  },
                  {
                    value: 'company',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.по_компании'),
                  },
                  {
                    value: 'contact',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.по_контакту'),
                  },
                ]}
              />
              <Select<DeadlineFilter>
                aria-label={i18n.t('crm.features.features.tasks.pages.taskspage.срок')}
                value={deadlineFilter}
                style={{ width: 150, fontSize: 12 }}
                onChange={setDeadlineFilter}
                options={[
                  {
                    value: 'all',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.все_сроки'),
                  },
                  {
                    value: 'overdue',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.просрочено'),
                  },
                  {
                    value: 'today',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.сегодня'),
                  },
                  {
                    value: 'week',
                    label: i18n.t('crm.features.features.tasks.pages.taskspage.7_дней'),
                  },
                ]}
              />
            </FilterBar>
          </div>
        ) : null}
      </PageHeader>

      <div className="flex flex-col gap-3">
        {mutationError ? (
          <Alert
            type="error"
            showIcon
            closable
            title={i18n.t('crm.features.features.tasks.pages.taskspage.ошибка_операции')}
            description={mutationError.message}
          />
        ) : null}

        {isLoading ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietListSkeleton rows={8} />
          </div>
        ) : isError ? (
          <ErrorState
            title={i18n.t(
              'crm.features.features.tasks.pages.taskspage.не_удалось_загрузить_задачи',
            )}
            error={error}
            onRetry={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietEmpty
              text={i18n.t(
                'crm.features.features.tasks.pages.taskspage.задач_нет_создайте_первую_кнопкой_новая_за',
              )}
            />
          </div>
        ) : viewMode === 'kanban' ? (
          <TaskKanbanBoard
            columns={kanbanColumns}
            isUpdating={statusMutation.isPending}
            onCreateInStatus={openCreate}
            onMoveTask={(taskId, toStatus) => statusMutation.mutate({ taskId, toStatus })}
          />
        ) : groups ? (
          <div className="flex flex-col gap-4">
            {groups.map(([label, groupRows]) => (
              <div key={label}>
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className="text-[12.5px] font-semibold text-[var(--ant-color-text)]"
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    {label}
                  </span>
                  <Tag
                    style={{
                      fontSize: 11,
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--ant-color-text-tertiary)',
                      background: 'var(--g-fill)',
                      borderColor: 'transparent',
                      padding: '0 7px',
                    }}
                  >
                    {groupRows.length}
                  </Tag>
                </div>
                <div className="crm-checkbox-square" style={LIST_SURFACE_STYLE}>
                  <TasksGrid rows={groupRows} {...gridProps} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="crm-checkbox-square" style={LIST_SURFACE_STYLE}>
            <TasksGrid rows={rows} {...gridProps} />
          </div>
        )}
      </div>

      {/* Монтируем только открытой: внутри висят запросы справочников и ws-канал сделок. */}
      {formOpen ? (
        <TaskFormModal
          open
          onClose={closeForm}
          task={editingTask}
          {...(initialStatus ? { initialStatus } : {})}
        />
      ) : null}
    </div>
  );
}
